import { SyncStatus } from '@boma/supplier-portal/shared/entities';

/**
 * Response model for ZRA invoice status
 */
export interface IZraInvoiceStatusResponse {
  /**
   * The order ID
   */
  orderId: string;

  /**
   * Current submission status
   */
  status: SyncStatus;

  /**
   * Timestamp of the last submission attempt
   */
  lastAttemptAt?: Date;

  /**
   * Error message if submission failed
   */
  errorMessage?: string;

  /**
   * ZRA invoice ID if submission was successful
   */
  zraInvoiceId?: number;

  /**
   * ZRA receipt signature
   */
  rcptSign?: string;

  /**
   * MRC number
   */
  mrcNo?: string;

  /**
   * ZRA SDC ID
   */
  sdcId?: string;

  /**
   * URL to the QR code for the invoice
   */
  qrCodeUrl?: string;

  /**
   * ZRA receipt number
   */
  receiptNumber?: string;

  /**
   * ZRA invoice number
   */
  invoiceNumber?: string;

  /**
   * ZRA internal data
   */
  intrlData?: string;

  /**
   * SDC Time
   */
  sdcTime?: string;

  /**
   * Bank account name
   */
  bankAccountName?: string;

  /**
   * Bank name
   */
  bankName?: string;

  /**
   * Swift code
   */
  swiftCode?: string;

  /**
   * Branch name
   */
  branchName?: string;

  /**
   * Account number
   */
  accountNumber?: string;

  /**
   * Number of items in the invoice
   */
  itemsCount?: number;

  /**
   * Document number
   */
  documentNumber?: string;

  /**
   * Taxable amount (total without VAT)
   */
  taxableAmount?: number;

  /**
   * Tax amount (VAT)
   */
  tax?: number;

  /**
   * Total amount (including VAT)
   */
  total?: number;

  /**
   * Exchange rate used for the invoice
   */
  exchangeRate?: string;

}

/**
 * Request model for getting status of multiple orders
 */
export interface IBulkOrderStatusRequest {
  /**
   * List of order IDs to get status for
   */
  orderIds: string[];
}

/**
 * ZRA details for a charge item
 */
export interface IChargeItemZraDetails {
  /**
   * The order charge item ID
   */
  orderChargeItemId: string;

  /**
   * ZRA item classification code
   */
  itemCode: string;

  /**
   * Discount rate as a percentage
   */
  discountRate?: number;

  /**
   * Item description
   */
  description?: string;
}

/**
 * Request model for creating a ZRA invoice
 */
export interface ICreateZraInvoiceRequest {
  /**
   * Additional notes (optional)
   */
  notes?: string;

  /**
   * Original invoice number (required for debit notes)
   */
  originalInvoiceNumber?: string;

  /**
   * Original ZRA invoice ID (required for debit notes)
   */
  originalInvoiceId?: number;
}

/**
 * Generic ZRA API response wrapper
 */
export interface IZraApiResponse<T> {
  errors: string[];
  message: string;
  succeeded: boolean;
  data: T;
}

/**
 * Paged ZRA API response wrapper
 */
export interface IZraPagedResponse<T> {
  errors: string[];
  message: string;
  succeeded: boolean;
  data: T[];
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * ZRA Item model
 */
export interface IZraItem {
  id: number;
  code: string;
  name: string;
  description: string;
  taxType: string;
  price: number;
  type: string;
  registeredWithZRA: boolean;
  updateZRA: boolean;
  quantity: number;
  taxRate: number;
  taxInclusive: boolean;
  recommendedRetailedPrice: number | null;
  status: number;
}

/**
 * ZRA Invoice model
 */
export interface IZraInvoice {
  id: number;
  number: string;
  description: string;
  subtotal: number;
  totalTax: number;
  totalAmount: number;
  receiptSignature: string;
  customerTPIN: string;
  receiptNumber: string | null;
  invoiceNumberZRAFormatted: string;
  lpoNumber: string | null;
  destinationCountry: string | null;
  reference: string | null;
  currency: string;
  exchangeRate: number;
  creditNoteStatus: number;
  dueDate: string;
  createdAt: string;
  customerName: string;
  status: number;
}

/**
 * ZRA Classification Code model
 */
export interface IZraClassificationCode {
  itemClsCd: string;
  itemClsNm: string;
  itemClsLvl: number;
  taxTyCd: string | null;
  mjrTgYn: string | null;
  useYn: string;
  searchString: string;
  id: number;
  name: string | null;
  createdAt: string;
  updatedAt: string;
}
